import _ from 'lodash'
import { CalcRegistry } from './CalcRegistry'
import { AnyConceptStatusesByCid, ConceptStatusValueType, ConceptStatusValidityByCid, Export, serializeFusionError } from '@fusion-advantage/backcompat'
import { ConceptActivation, ConceptActivationByCid } from '../concept/ConceptActivation'
import { getConceptStatusValue, getSubconceptStatusValue } from '../concept/getConceptStatus'
import { LoopDetector } from '../LoopDetector'
import { ReduxRunContext } from './scriptevaluator/RunContext'
import { ConceptsByCid } from '../concept/Concept'
import { ConceptStatusUpdate } from '../concept/ConceptStatusUpdate'
import { err, ok, Result } from 'neverthrow'
import { CalcRuntimeError, CalcScriptRuntimeErrorMsgs, ValueCalcRuntimeReturnValueItem } from './scriptevaluator/RuntimeTypes'
import { ExecutableCalc, ExecutableCalcCalculateOk } from './executable/ExecutableTypes'
import { CalcType } from './scriptcompiler/CompilerTypes'

export type CalcEngineState = {
	export: Export
	concepts: ConceptsByCid
	statuses: AnyConceptStatusesByCid
	prevStatuses: AnyConceptStatusesByCid
}

export enum CalculationRunType {
	Initial = 'initial',
	// otherwise calculations should only be run when triggered by
	// a change to their input concept status
	Affected = 'affected',
}

interface RunCalculationsArgs {
	engineState: CalcEngineState
	runType: CalculationRunType
}

interface RunCalculationsOk {
	results: Map<string, Result<ExecutableCalcCalculateOk, CalcRuntimeError>>
	statusUpdates: ConceptStatusUpdate[]
	validityUpdates: ConceptStatusValidityByCid
	activationUpdates: ConceptActivationByCid
}

export class CalcEngine {
	private isUsed = false
	private registry: CalcRegistry
	private statusUpdates: ConceptStatusUpdate[] = []
	private validityUpdates: ConceptStatusValidityByCid = {}
	private activationUpdates: ConceptActivationByCid = {}
	private loopDetector: LoopDetector

	constructor(registry: CalcRegistry, loopDetector: LoopDetector) {
		this.registry = registry
		this.loopDetector = loopDetector
	}

	runCalculations({engineState, runType}: RunCalculationsArgs): Result<RunCalculationsOk, CalcRuntimeError[]> {
		if(this.isUsed) {
			return err([{
				area: 'CalcEngine.runCalculations',
				message: 'Attempted to reuse instance',
			}])
		}
		this.isUsed = true
		const context = new ReduxRunContext(engineState)
		let calculationsToRun: ExecutableCalc[] = []
		switch(runType) {
			// just after form data is loaded, execute all validation and
			// activation calcs once to ensure all controls are set up
			// properly—
			// do not execute value calculations as the data is already correct
			// and executing the calculations without being driven by actual
			// changes could cause unwanted (or _very_ unwanted) data changes
			// especially when an existing encounter is opened back up
			case CalculationRunType.Initial:
				calculationsToRun = this.registry.getActiveCalcs().filter(
					calc => (calc.calcType === CalcType.Validation || calc.calcType === CalcType.Activation)
				)
				break
			// if at some point in the future there feels a need to have a
			// value calculation fire on or before form load, please hold an
			// architecture discussion...  this was resolved previously with
			// evaluator signature wipes by wiping on the previous submit when
			// we knew it was going to have to get resigned in the next
			// workflow step (CodeNext)...  much cleaner than a one-off
			// value calc on load
			case CalculationRunType.Affected:
				calculationsToRun = this.registry.getAffectedCalcs(context.getChangedInputCids())
				break
			default:
				return err([{
					area: 'CalcEngine.runCalculations',
					message: `Unknown run type ${runType}`,
				}])
		}

		const results = new Map<string, Result<ExecutableCalcCalculateOk,CalcRuntimeError>>()
		for (const calc of calculationsToRun) {
			if (this.loopDetector.push(calc.calcKey)) {
				debugger
				return err([{
					area: 'CalcEngine.runCalculations',
					message: `Loop detected on calc '${calc.calcKey}'! History ${JSON.stringify(this.loopDetector.prevItems)}`,
				}])
			}
			const rExec = this.runCalculation(context, calc)
			results.set(calc.calcKey, rExec)
		}
		return ok({
			results,
			statusUpdates: this.statusUpdates,
			validityUpdates: this.validityUpdates,
			activationUpdates: this.activationUpdates,
		})
	}

	private runCalculation(context: ReduxRunContext, calc: ExecutableCalc): Result<ExecutableCalcCalculateOk, CalcRuntimeError> {
		console.debug(`[CalcEngine] running ${calc.calcKey}`)
		let execOk: ExecutableCalcCalculateOk
		// try is last resort: catch anything legacy or really unexpected
		try {
			const rExec = calc.calculate(context)
			if(rExec.isErr()) {
				return(err(rExec.error))
			}
			execOk = rExec.value
		} catch(ex) {
			const error = serializeFusionError('caught', ex)
			return err({
				area: 'CalcEngine.runCalculation',
				message: JSON.stringify(error),
			})
		}
		const { calcType: evalCalcType, returnNode, returnValue } = execOk
		if(evalCalcType !== calc.calcType) {
			return err({
				area: 'CalcEngine.runCalculations',
				message: CalcScriptRuntimeErrorMsgs.ReturnStatementInferredChangedCalcType(calc.calcType, evalCalcType),
				line: returnNode?.line,
				column: returnNode?.column,
			})
		}
		switch(evalCalcType) {
			case CalcType.Value: {
				returnValue.forEach(item => {
					console.debug('[CalcEngine] value ->', calc.outputCid, item)
					this.setValue(context, calc.outputCid, item)
				})
				break
			}
			case CalcType.Validation: {
				console.debug('[CalcEngine] validity ->', calc.outputCid, returnValue)
				this.validityUpdates[calc.outputCid] = returnValue
				break
			}
			case CalcType.Activation: {
				console.debug('[CalcEngine] activation ->', calc.outputCid, returnValue)
				this.setActivation(context, calc.outputCid, returnValue)
				break
			}
		}
		return ok(execOk)
	}

	private pushStatusUpdate(context: ReduxRunContext, update: ConceptStatusUpdate) {
		// don't resubmit the same value
		const existingStatus = context.statuses[update.cid]
		if (update.subconceptCid) {
			if (getSubconceptStatusValue(existingStatus, update.subconceptCid) === update.value) {
				return
			}
		} else if (getConceptStatusValue(existingStatus) === update.value) {
			return
		}

		this.statusUpdates.push(update)
	}

	private setValue(context: ReduxRunContext, cid: string, result: ValueCalcRuntimeReturnValueItem) {
		if (result.value === undefined) {
			return
		}

		this.pushStatusUpdate(context, {
			cid,
			subconceptCid: result.subconceptCid,
			value: result.value,
			valueType: ConceptStatusValueType.Derived,
		})
	}

	private setActivation(context: ReduxRunContext, cid: string, activation: ConceptActivation) {
		this.activationUpdates[cid] = activation
		// TODO this was enabled to delete form data where the control had
		// been hidden or disabled, but it is quite imperative and adds
		// unpredictability to the engine's results.
		// will experiment with something like a new default calculation
		// (or maybe just some code in this Engine) that drops the data for
		// concepts that have no value calc, and a function to be used in
		// existing value calcs to predictably catch this case and handle it
		// if (!shouldPersist(activation)) {
		// 	const existingStatus = context.statuses[cid]
		// 	if (isSuperconceptStatus(existingStatus)) {
		// 		_.forEach(existingStatus.subconcepts, (substatus, subconceptCid) => {
		// 			this.pushStatusUpdate(context, {
		// 				cid,
		// 				subconceptCid,
		// 				value: null,
		// 				valueType: ConceptStatusValueType.Derived,
		// 			})
		// 		})
		// 	} else if (isConceptStatus(existingStatus)) {
		// 		this.pushStatusUpdate(context, {
		// 			cid,
		// 			value: null,
		// 			valueType: ConceptStatusValueType.Derived,
		// 		})
		// 	}
		// }
	}
}